import { EditOutlined } from "@ant-design/icons";
import { InputNumber, InputRef } from "antd";
import OffsetIcon from "components/svg-icons/offset-icon";
import { TLinkSource } from "components/feature-entity-linking-page/models";
import { useEffect, useMemo, useRef, useState } from "react";
import { DATE_FORMAT_MMDDYYYY, DateUtils } from "utils/dateutils";
import { restrictOffsetInputToNumbers } from "utils/inpututils";

function OffsetDurationInputBox(props: {
  task: TLinkSource;
  canEdit: boolean;
  onOffsetChange: (prop: {
    offset: number | null;
    activityLink: TLinkSource;
  }) => void;
  openToEdit: boolean;
}) {
  const { task, onOffsetChange, canEdit, openToEdit } = props;
  const [isEditing, setEditing] = useState(openToEdit && canEdit);
  const inputRef: any = useRef<InputRef>(null);
  const [offset, setOffset] = useState(task.offset_duration);
  const offsetLimit = 200;

  useEffect(() => {
    setOffset(task.offset_duration);
  }, [task.offset_duration]);

  const getSign = useMemo(() => {
    return (offset ?? 0) > 0 ? "+" : "";
  }, [offset]);

  const editOffsetComponent = () => {
    return (
      <div className="flex items-center space-x-1 bg-ciq-gray-500 h-[30px] px-1 rounded-xs  ">
        <div className="flex text-sm items-center text-black text-opacity-90 font-normal">
          Offset:
          <InputNumber
            className="ml-1"
            style={{ width: 205 }}
            defaultValue={task.offset_duration || 0}
            placeholder="-2"
            autoFocus
            type="number"
            min={-offsetLimit}
            max={offsetLimit}
            ref={inputRef}
            size="small"
            addonAfter="days"
            data-testid="offset-input"
            onBlur={(event) => {
              const value = Number(event?.target?.value || 0);
              if (!value) {
                setEditing(false);
                return;
              }
              if (value > offsetLimit || value < -offsetLimit) {
                inputRef?.current?.focus();
                return;
              }

              onOffsetChange({
                offset: Number(value),
                activityLink: task
              });
              setEditing(false);
            }}
            onChange={(value) => {
              setOffset(value);
            }}
            onPressEnter={(e) => {
              (e.target as HTMLInputElement).blur();
            }}
            onKeyDown={restrictOffsetInputToNumbers}
          />
        </div>
      </div>
    );
  };
  const cssForView = useMemo(() => {
    const fixedcss =
      "flex items-center space-x-1 bg-ciq-gray-500 h-[32px] px-3 rounded-sm";

    return `${
      offset === 0 && !isEditing
        ? "border-ciq-red-800 border-solid border-[1px]"
        : "border-none"
    } ${fixedcss}`;
  }, [offset, isEditing]);
  return (
    <div className={cssForView}>
      <OffsetIcon />
      {isEditing ? (
        editOffsetComponent()
      ) : (
        <div className="text-black text-opacity-90 font-normal space-x-1 border">
          <span data-testid="offset-label">
            Offset: {getSign}
            <span
              className={`${
                offset === 0 ? "text-ciq-red-800" : "text-black text-opacity-90"
              }`}
              data-testid="offset-value"
            >
              {offset} {offset === 1 ? "day" : "days"}
            </span>
          </span>
          <span className="opacity-80" data-testid="offset-date">
            {task?.linked_offset_date && offset !== 0
              ? `(${DateUtils.format(
                  task?.linked_offset_date,
                  DATE_FORMAT_MMDDYYYY
                )})`
              : ""}
          </span>
          {canEdit && (
            <EditOutlined
              onClick={() => {
                if (!offset) {
                  onOffsetChange({
                    offset: Number(offset),
                    activityLink: task
                  });
                }
                setEditing(!isEditing);
              }}
              className="cursor-pointer"
              data-testid="edit-offset-button"
            />
          )}
        </div>
      )}
    </div>
  );
}

export default OffsetDurationInputBox;
